import React, { useContext } from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ROUTE_PLANS, ROUTE_SUBSCRIPTIONS } from "../../constants/routes";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AnimatedContainer from "../common/AnimatedContainer";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  BusinessTypeNamingMapping,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import { useGetSubscriptionList } from "../../hooks/api/useSubscription";
import type { RootState } from "../../store/store";
import { isKnownNonManagerRole } from "../../Utils/membershipRoleUtils";

const MembershipHome: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { config } = usePlatformConfig();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const role = useSelector((state: RootState) => state.user.userDetails?.role);
  const getSubscriptionListMutation = useGetSubscriptionList();

  const handleGetStarted = async () => {
    if (isKnownNonManagerRole(role)) {
      navigate(localizedPath(ROUTE_PLANS, language, defaultLang));
      return;
    }

    try {
      const res = await getSubscriptionListMutation.mutateAsync({});
      const list = res.listSubscriptions.subscriptionList ?? [];
      const hasActiveSubscription = list.some((sub) => sub.status === "ACTIVE");
      navigate(
        localizedPath(
          hasActiveSubscription ? ROUTE_SUBSCRIPTIONS : ROUTE_PLANS,
          language,
          defaultLang
        )
      );
    } catch {
      navigate(localizedPath(ROUTE_PLANS, language, defaultLang));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)"
            }
          }
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-30%",
          left: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)"
            }
          }
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: 4,
            pt: { xs: 4, md: 8 }
          }}
        >
          {/* Left Section - Text and CTA */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "0 1 60%" },
              width: { xs: "100%", md: "60%" }
            }}
          >
            <AnimatedContainer
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Box
                sx={{
                  color: "white",
                  textAlign: { xs: "center", md: "left" }
                }}
              >
                {/* Main Heading */}
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: {
                      xs: "1.25rem",
                      sm: "1.60rem",
                      md: "2.65rem",
                      lg: "2.75rem"
                    },
                    lineHeight: 1.3,
                    mb: 3,
                    color: "white",
                    letterSpacing: { xs: "0.01em", md: "0.02em" },
                    textShadow: "0 2px 10px rgba(0,0,0,0.2)"
                  }}
                >
                  {t(
                    "membership-heading",
                    "Select the best plan for your needs"
                  )}
                </Typography>

                {config?.businessType != null && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      color: "rgba(255, 255, 255, 0.85)",
                      fontWeight: 500
                    }}
                  >
                    {BusinessTypeNamingMapping[config.businessType]}
                  </Typography>
                )}

                {/* Body Text */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                    lineHeight: 1.7,
                    mb: 5,
                    color: "rgba(255, 255, 255, 0.9)",
                    maxWidth: { xs: "100%", md: "85%" },
                    fontWeight: 400
                  }}
                >
                  {t(
                    "membership-description",
                    "To access all features of the system, you need to subscribe to a plan. Choose a plan that best fits your business needs and unlock the full potential of our platform."
                  )}
                </Typography>

                {/* CTA Button */}
                <AnimatedContainer
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
                >
                  <Button
                    variant="contained"
                    onClick={handleGetStarted}
                    sx={{
                      backgroundColor: BRAND_COLOR,
                      color: "white",
                      border: "none",
                      py: 2,
                      px: 6,
                      borderRadius: 3,
                      fontSize: { xs: "0.95rem", md: "1.1rem" },
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      boxShadow: "0 4px 15px rgba(147, 109, 174, 0.4)",
                      "&:hover": {
                        backgroundColor: BRAND_HOVER,
                        transform: "translateY(-3px)",
                        boxShadow: "0 10px 25px rgba(147, 109, 174, 0.5)"
                      },
                      transition: "all 0.3s ease"
                    }}
                  >
                    {t("membership-get-started", "Get started")}
                  </Button>
                </AnimatedContainer>
              </Box>
            </AnimatedContainer>
          </Box>

          {/* Right Section - Money Icon */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "0 1 40%" },
              width: { xs: "100%", md: "40%" },
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: { xs: "center", md: "flex-end" },
              minHeight: { xs: "200px", md: "400px" },
              position: "relative"
            }}
          >
            <AnimatedContainer
              initial={{ opacity: 0, x: 50, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            >
              {/* Animated Money Icon - positioned bottom right */}
              <Box
                sx={{
                  position: "relative",
                  animation: "pulse 3s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      transform: "scale(1) rotate(0deg)"
                    },
                    "50%": {
                      transform: "scale(1.05) rotate(3deg)"
                    }
                  }
                }}
              >
                <AttachMoneyIcon
                  sx={{
                    fontSize: {
                      xs: "100px",
                      sm: "130px",
                      md: "160px",
                      lg: "200px"
                    },
                    color: "rgba(255, 255, 255, 0.15)",
                    filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.2))"
                  }}
                />
                <AttachMoneyIcon
                  sx={{
                    fontSize: {
                      xs: "100px",
                      sm: "130px",
                      md: "160px",
                      lg: "200px"
                    },
                    color: "rgba(255, 255, 255, 0.15)",
                    filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.2))"
                  }}
                />
              </Box>
            </AnimatedContainer>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MembershipHome;
